import { forwardRef, type ReactNode } from 'react';

import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

type SettingsSectionProps = {
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
};

/**
 * Two-column settings section (prototype-settings-checkout.html): left column
 * is a short title + one-line description, right column holds the content
 * (usually a SettingsCard). Collapses to a single column on narrow screens.
 * The forwarded ref targets the whole section (deep-link highlight ring).
 */
export const SettingsSection = forwardRef<HTMLElement, SettingsSectionProps>(
  ({ title, description, children, className }, ref) => (
    <section
      ref={ref}
      className={cn(
        'grid items-start gap-3 rounded-xl md:grid-cols-[220px_1fr] md:gap-7',
        className,
      )}
    >
      <div className="md:pt-1">
        <h3 className="text-sm font-semibold text-black">{title}</h3>
        {description ? (
          <p className="mt-1 text-[13px] leading-snug text-muted-foreground">{description}</p>
        ) : null}
      </div>
      <div className="min-w-0">{children}</div>
    </section>
  ),
);
SettingsSection.displayName = 'SettingsSection';

/**
 * White card holding one or more setting rows; direct children are separated
 * by hairline dividers and manage their own vertical padding.
 */
export function SettingsCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'divide-y divide-[#efefec] rounded-xl border border-[#e5e5e5] bg-white px-5',
        className,
      )}
    >
      {children}
    </div>
  );
}

type SettingsToggleRowProps = {
  label: string;
  description?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  children?: ReactNode;
};

/** Card row: label + one-line subtitle with a Switch; optional expanded content below. */
export function SettingsToggleRow({
  label,
  description,
  checked,
  onCheckedChange,
  children,
}: SettingsToggleRowProps) {
  return (
    <div className="py-4">
      <div className="flex items-center gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-medium text-black">{label}</span>
          {description ? (
            <span className="text-[13px] text-muted-foreground">{description}</span>
          ) : null}
        </div>
        <Switch checked={checked} onCheckedChange={onCheckedChange} aria-label={label} />
      </div>
      {children ? <div className="mt-3">{children}</div> : null}
    </div>
  );
}

/** Card row: label + optional one-line hint on the left, control(s) on the right. */
export function SettingsRow({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-3 py-4">
      <div className="min-w-[180px] flex-1">
        <span className="block text-sm font-medium text-black">{label}</span>
        {description ? (
          <span className="mt-0.5 block text-[13px] text-muted-foreground">{description}</span>
        ) : null}
      </div>
      {children}
    </div>
  );
}

/** Indented inline-controls row shown under an enabled toggle ("Within [5] km · after [5] minutes"). */
export function InlineControlRow({ children }: { children: ReactNode }) {
  return (
    <div className="ml-4 flex flex-wrap items-center gap-2 border-l-2 border-[#f0e6bb] pl-3.5 text-[13px] text-muted-foreground">
      {children}
    </div>
  );
}

/** Compact centered number input used inside InlineControlRow. */
export const inlineNumberInputClass =
  'h-8 w-16 rounded-lg bg-white text-center text-[13px] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';
